import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Search, UsersIcon, AlertTriangle } from "lucide-react";

import {
  getAccommodations,
  createAccommodation,
  updateAccommodation,
  deleteAccommodation,
} from "../../../services/travel/accommodationService";

import AccommodationForm from "./AccommodationForm";
import AccommodationDetailsModal from "./AccommodationDetailsModal";
import { normalizeAccommodationImages } from "./dummyAccommodationImages";
import { CustomDatePicker } from "../../CustomReusables/CustomDatePicker";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { useAuth } from "../../../auth/AuthContext";
import BusinessListingShowcaseCard from "../../Businesses/BusinessListingShowcaseCard";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const cardsBorderRadii = "8px";

const toISO = (d: Date) => d.toISOString().slice(0, 10);

const cleanOwnerId = (value: any) => {
  const id = String(value || "").trim();

  if (!id) return "";
  if (id.toLowerCase() === "guest") return "";

  return id;
};

const getValidOwnerId = (acc: any) => {
  return cleanOwnerId(
    acc?.ownerId ||
      acc?.ownerUserId ||
      acc?.createdBy ||
      acc?.sellerUserId ||
      acc?.userId ||
      "",
  );
};

type FeedbackModalState = {
  isOpen: boolean;
  title: string;
  description: string;
};

const Accommodations = () => {
  const { theme } = useCustomTheme();
  const { user } = useAuth();
  const [accommodations, setAccommodations] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);

  const [query, setQuery] = useState("");
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState<number>(1);
  const [dateValidationAttempted, setDateValidationAttempted] = useState(false);
  const availabilityControlsRef = useRef<HTMLDivElement | null>(null);

  const [availabilityMap, setAvailabilityMap] = useState<
    Record<string, boolean>
  >({});
  const [searching, setSearching] = useState(false);
  const [availabilityMode, setAvailabilityMode] = useState(false);
  const [loadingAccommodations, setLoadingAccommodations] = useState(true);

  const formModal = useDisclosure();
  const detailsModal = useDisclosure();
  const navigate = useNavigate();

  const [feedbackModal, setFeedbackModal] = useState<FeedbackModalState>({
    isOpen: false,
    title: "",
    description: "",
  });

  const showFeedbackModal = (title: string, description: string) => {
    setFeedbackModal({
      isOpen: true,
      title,
      description,
    });
  };

  const closeFeedbackModal = () => {
    setFeedbackModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const currentUserUid = String(user?.uid || "").trim();
  const currentUserEmail = String(user?.email || "").trim();

  const attachOwnerToAccommodation = (acc: any, fallbackOwnerId?: string) => {
    const ownerId = getValidOwnerId(acc) || cleanOwnerId(fallbackOwnerId);
    const ownerEmail = String(acc?.ownerEmail || "").trim();

    return {
      ...acc,

      // Preserve ownership details when they exist, but never use the
      // current viewer/buyer as the accommodation owner.
      userId: ownerId || acc?.userId || "",
      ownerId,
      ownerUserId: ownerId,
      sellerUserId: ownerId,
      createdBy: cleanOwnerId(acc?.createdBy) || ownerId,

      ...(ownerEmail ? { ownerEmail } : {}),
    };
  };

  const isOwner = (acc: any) => {
    const ownerId = getValidOwnerId(acc);

    return (
      !!currentUserUid &&
      (ownerId === currentUserUid ||
        String(acc?.userId || "") === currentUserUid ||
        String(acc?.ownerId || "") === currentUserUid ||
        String(acc?.ownerUserId || "") === currentUserUid ||
        String(acc?.sellerUserId || "") === currentUserUid ||
        String(acc?.createdBy || "") === currentUserUid ||
        (!!currentUserEmail &&
          String(acc?.ownerEmail || "") === currentUserEmail))
    );
  };

  const fetchAccommodations = async () => {
    try {
      setLoadingAccommodations(true);

      const response = await getAccommodations();
      const data = response.data || [];

      const normalized = data.map((a: any) => {
        const images = normalizeAccommodationImages(a.images, a.imageUrl);
        const ownerId = getValidOwnerId(a);

        return {
          ...a,
          images,
          availability: a.availability !== false,
          pricePerNight: a.pricePerNight ?? a.price ?? 0,
          type: a.type || a.roomType || "Accommodation",

          // Keep owner details from DB. Do not replace with the current logged-in user here.
          ownerId,
          ownerUserId: ownerId,
          sellerUserId: ownerId,
          userId: ownerId || a.userId || "",
          createdBy: cleanOwnerId(a.createdBy) || ownerId,
        };
      });

      setAccommodations(normalized);
      setAvailabilityMap({});
      setAvailabilityMode(false);
    } finally {
      setLoadingAccommodations(false);
    }
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-accommodation-date-zindex-fix", "true");
    style.innerHTML = `

      /*
       * Keep every React DatePicker above cards, maps and listing controls.
       * The white background prevents page content from showing through it.
       */
      .react-datepicker-popper {
        z-index: 2147483647 !important;
      }

      .react-datepicker__portal,
      .react-datepicker__tab-loop {
        z-index: 2147483647 !important;
      }

      .react-datepicker {
        z-index: 2147483647 !important;
        background: #ffffff !important;
        border-color: #CBD5E0 !important;
        box-shadow:
          0 20px 25px -5px rgba(0, 0, 0, 0.18),
          0 10px 10px -5px rgba(0, 0, 0, 0.08) !important;
        opacity: 1 !important;
      }

      .react-datepicker__month-container,
      .react-datepicker__header,
      .react-datepicker__time-container,
      .react-datepicker__time,
      .react-datepicker__time-box,
      .react-datepicker__time-list {
        background: #ffffff !important;
        opacity: 1 !important;
      }

      .react-datepicker__header {
        border-bottom-color: #E2E8F0 !important;
      }

      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header,
      .react-datepicker__day-name,
      .react-datepicker__day:not(.react-datepicker__day--selected):not(.react-datepicker__day--keyboard-selected):not(.react-datepicker__day--in-range):not(.react-datepicker__day--in-selecting-range),
      .react-datepicker__time-list-item:not(.react-datepicker__time-list-item--selected) {
        color: #1A202C !important;
      }

      .react-datepicker__day--outside-month:not(.react-datepicker__day--selected) {
        color: #A0AEC0 !important;
      }

      .react-datepicker__triangle {
        fill: #ffffff !important;
        color: #ffffff !important;
        stroke: #CBD5E0 !important;
      }

      .react-datepicker-popper[data-placement^="bottom"]
        .react-datepicker__triangle::before,
      .react-datepicker-popper[data-placement^="bottom"]
        .react-datepicker__triangle::after {
        border-bottom-color: #ffffff !important;
      }

      .react-datepicker-popper[data-placement^="top"]
        .react-datepicker__triangle::before,
      .react-datepicker-popper[data-placement^="top"]
        .react-datepicker__triangle::after {
        border-top-color: #ffffff !important;
      }

      .react-datepicker-wrapper,
      .react-datepicker__input-container {
        width: 100% !important;
      }

    `;
    document.head.appendChild(style);

    return () => style.remove();
  }, []);

  useEffect(() => {
    fetchAccommodations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (data: any) => {
    if (!currentUserUid) return;

    /**
     * Creating accommodation:
     * The logged-in user is the listing owner.
     */
    const payload = {
      ...data,
      userId: currentUserUid,
      ownerId: currentUserUid,
      ownerUserId: currentUserUid,
      sellerUserId: currentUserUid,
      createdBy: currentUserUid,
      ...(currentUserEmail ? { ownerEmail: currentUserEmail } : {}),
    };

    await createAccommodation(payload);
    fetchAccommodations();
    formModal.onClose();
  };

  const handleUpdate = async (data: any) => {
    if (!selected || !currentUserUid) return;

    /**
     * Updating accommodation:
     * Keep existing real owner.
     * If this is an old broken accommodation with guest/missing owner,
     * saving from the owner account repairs ownership.
     */
    const existingOwnerId = getValidOwnerId(selected);
    const finalOwnerId = existingOwnerId || currentUserUid;

    const payload = {
      ...data,
      userId: finalOwnerId,
      ownerId: finalOwnerId,
      ownerUserId: finalOwnerId,
      sellerUserId: finalOwnerId,
      createdBy: cleanOwnerId(selected?.createdBy) || finalOwnerId,
      ...(currentUserEmail
        ? { ownerEmail: selected?.ownerEmail || currentUserEmail }
        : {}),
    };

    await updateAccommodation(selected._id, payload);
    fetchAccommodations();
    formModal.onClose();
  };

  const handleDelete = async (id: string) => {
    await deleteAccommodation(id);
    fetchAccommodations();
  };

  const dateValidation = useMemo(() => {
    let checkInError = "";
    let checkOutError = "";

    if (!checkIn) {
      checkInError = "Check-in date is required.";
    }

    if (!checkOut) {
      checkOutError = "Check-out date is required.";
    }

    if (checkIn && checkOut && checkOut <= checkIn) {
      checkOutError = "Check-out must be after check-in.";
    }

    return {
      checkInError,
      checkOutError,
      isValid: !checkInError && !checkOutError,
    };
  }, [checkIn, checkOut]);

  const resetAvailability = () => {
    setAvailabilityMode(false);
    setAvailabilityMap({});
  };

  const filteredText = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accommodations;

    return accommodations.filter((a) =>
      `${a.name || ""} ${a.location || ""} ${a.type || ""} ${a.roomType || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [accommodations, query]);

  const filtered = useMemo(() => {
    /*
     * Keep unavailable properties visible so customers can still view them.
     * Availability only changes the badge and whether booking is allowed.
     */
    return filteredText;
  }, [filteredText]);

  const getAccommodationAvailabilityState = (acc: any) => {
    const roomOptions = Array.isArray(acc?.roomOptions) ? acc.roomOptions : [];

    const anyRoomInventory =
      roomOptions.length > 0
        ? roomOptions.some((room: any) => Number(room?.availableRooms || 0) > 0)
        : Number(acc?.rooms || 0) > 0;

    const manuallyAvailable = acc?.availability !== false && anyRoomInventory;

    const available = availabilityMode
      ? availabilityMap[acc?._id] === true
      : manuallyAvailable;

    const statusLabel = available
      ? "Available"
      : availabilityMode
        ? "Sold out"
        : acc?.availability === false
          ? "Unavailable"
          : "Sold out";

    return {
      available,
      statusLabel,
      anyRoomInventory,
    };
  };

  const openDetails = (acc: any) => {
    const { available, statusLabel } = getAccommodationAvailabilityState(acc);

    const safeAccommodation = attachOwnerToAccommodation(acc);

    setSelected({
      ...safeAccommodation,
      availability: available,
      checkoutCanBook: available,
      availabilityStatusLabel: statusLabel,
      unavailableReason: available
        ? ""
        : availabilityMode
          ? "This accommodation has no rooms available for the selected dates."
          : "This accommodation is currently unavailable.",
      rooms: available ? safeAccommodation?.rooms : 0,
      roomOptions: Array.isArray(safeAccommodation?.roomOptions)
        ? safeAccommodation.roomOptions.map((room: any) => ({
            ...room,
            availableRooms: available ? Number(room?.availableRooms || 0) : 0,
          }))
        : safeAccommodation?.roomOptions,
    });

    detailsModal.onOpen();
  };

  const goToCheckout = (payload: any) => {
    const rawAccommodation = payload?.accommodation || payload;
    const accommodation = attachOwnerToAccommodation(rawAccommodation);
    const room = payload?.room || null;
    const ci = payload?.checkIn || (checkIn ? toISO(checkIn) : null);
    const co = payload?.checkOut || (checkOut ? toISO(checkOut) : null);
    const g = payload?.guests ?? guests;

    if (!accommodation?._id) return;

    if (
      accommodation?.checkoutCanBook === false ||
      accommodation?.availability === false
    ) {
      showFeedbackModal(
        accommodation?.availabilityStatusLabel || "Accommodation unavailable",
        accommodation?.unavailableReason ||
          "This accommodation cannot be booked for the selected dates.",
      );
      return;
    }

    const ownerId = getValidOwnerId(accommodation);

    const checkoutAccommodation = {
      ...accommodation,
      ...(ownerId
        ? {
            userId: ownerId,
            ownerId,
            ownerUserId: ownerId,
            sellerUserId: ownerId,
            createdBy: cleanOwnerId(accommodation?.createdBy) || ownerId,
          }
        : {}),
    };

    detailsModal.onClose();

    navigate(`/travel/accommodations/checkout/${accommodation._id}`, {
      state: {
        accommodation: checkoutAccommodation,
        room,
        checkIn: ci,
        checkOut: co,
        guests: g,
      },
    });
  };

  const searchAvailability = async () => {
    if (!API_BASE) return;

    if (!checkIn || !checkOut || checkOut <= checkIn) {
      showFeedbackModal(
        "Select valid stay dates",
        "Please select a valid check-in date and a check-out date that is after check-in.",
      );
      return;
    }

    const ci = toISO(checkIn);
    const co = toISO(checkOut);

    setSearching(true);
    try {
      const results: Record<string, boolean> = {};
      const list = accommodations;

      await Promise.all(
        list.map(async (acc) => {
          try {
            const res = await axios.get(
              `${API_BASE}/api/v1/travel/accommodations/${acc._id}/availability`,
              { params: { checkIn: ci, checkOut: co, guests } },
            );

            const arr = res.data?.availability || [];
            const anyAvailable = arr.some(
              (x: any) => Number(x.available || 0) > 0,
            );
            results[acc._id] = anyAvailable;
          } catch {
            results[acc._id] = false;
          }
        }),
      );

      setAvailabilityMap(results);
      setAvailabilityMode(true);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (!dateValidation.isValid) {
      resetAvailability();
      return;
    }

    const timer = window.setTimeout(() => {
      void searchAvailability();
    }, 350);

    return () => window.clearTimeout(timer);
    // searchAvailability intentionally runs only when stay inputs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkIn,
    checkOut,
    guests,
    accommodations.length,
    dateValidation.isValid,
  ]);

  const clearAvailabilityFilter = () => {
    setAvailabilityMode(false);
    setAvailabilityMap({});
  };

  const showMainDateValidation = () => {
    setDateValidationAttempted(true);

    window.requestAnimationFrame(() => {
      availabilityControlsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const handleAccommodationAction = (acc: any, available: boolean) => {
    if (!dateValidation.isValid) {
      showMainDateValidation();
      return;
    }

    if (searching || !availabilityMode) {
      void searchAvailability();
      return;
    }

    if (!available) {
      openDetails(acc);
      return;
    }

    openDetails(acc);
  };

  return (
    <Box
      borderRadius={cardsBorderRadii}
      boxShadow={theme.shadowSm}
      bg={theme.surface}
      minH="100vh"
      border="1px solid"
      borderColor={theme.border}
    >
      <Box px={4} pt={4} position="relative" zIndex={1000}>
        <Box
          ref={availabilityControlsRef}
          display="grid"
          gridTemplateColumns={{
            base: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "minmax(220px, 1fr) 190px 190px 180px",
            xl: "minmax(220px, 1fr) 190px 190px 180px max-content",
          }}
          gap={3}
          alignItems={{ base: "stretch", lg: "end" }}
          pt={4}
          position="relative"
          w="100%"
        >
          <Box w="100%" minW={0}>
            <Flex
              alignItems="center"
              color={theme.textMuted}
              gap={2}
              mb={1}
              pl="2px"
              minH="18px"
            >
              <Search size="11" />
              <Text fontSize="11px" color={theme.textSubtle}>
                Search
              </Text>
            </Flex>

            <Input
              w="100%"
              h="42px"
              placeholder="Search by name or location/address..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              bg={theme.inputBg}
              border="1px solid"
              borderColor={theme.inputBorder}
              borderRadius={cardsBorderRadii}
              fontWeight="600"
              _placeholder={{ color: theme.inputPlaceholder }}
              color={theme.text}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          </Box>

          <Box
            w="100%"
            minW={0}
            position="relative"
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
          >
            {dateValidationAttempted && dateValidation.checkInError && (
              <Text
                mb={1}
                fontSize="xs"
                lineHeight="14px"
                color={theme.danger}
                noOfLines={1}
              >
                {dateValidation.checkInError}
              </Text>
            )}

            <Box
              w="100%"
              className={
                dateValidationAttempted && dateValidation.checkInError
                  ? "accommodation-date-input accommodation-date-input--invalid"
                  : "accommodation-date-input"
              }
              sx={
                dateValidationAttempted && dateValidation.checkInError
                  ? {
                      "& input": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                        borderRadius: "8px !important",
                      },
                      "& input:hover, & input:focus": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                      },
                    }
                  : undefined
              }
            >
              <CustomDatePicker
                width="100%"
                label={
                  dateValidationAttempted && dateValidation.checkInError
                    ? ""
                    : "Check-in"
                }
                selected={checkIn}
                onChange={(d) => {
                  setCheckIn(d);
                  resetAvailability();

                  if (d && checkOut && checkOut <= d) {
                    setCheckOut(null);
                  }
                }}
                placeholder="Check-in date"
              />
            </Box>
          </Box>

          <Box
            w="100%"
            minW={0}
            position="relative"
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
          >
            {dateValidationAttempted && dateValidation.checkOutError && (
              <Text
                mb={1}
                fontSize="xs"
                lineHeight="14px"
                color={theme.danger}
                noOfLines={1}
              >
                {dateValidation.checkOutError}
              </Text>
            )}

            <Box
              w="100%"
              className={
                dateValidationAttempted && dateValidation.checkOutError
                  ? "accommodation-date-input accommodation-date-input--invalid"
                  : "accommodation-date-input"
              }
              sx={
                dateValidationAttempted && dateValidation.checkOutError
                  ? {
                      "& input": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                        borderRadius: "8px !important",
                      },
                      "& input:hover, & input:focus": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                      },
                    }
                  : undefined
              }
            >
              <CustomDatePicker
                width="100%"
                label={
                  dateValidationAttempted && dateValidation.checkOutError
                    ? ""
                    : "Check-out"
                }
                selected={checkOut}
                onChange={(d) => {
                  setCheckOut(d);
                  resetAvailability();
                }}
                placeholder="Check-out date"
              />
            </Box>
          </Box>

          <Box w="100%" minW={0}>
            <Flex
              alignItems="center"
              color={theme.textMuted}
              gap={2}
              mb={1}
              pl="2px"
              minH="18px"
            >
              <UsersIcon size="11" />
              <Text fontSize="11px" color={theme.textSubtle}>
                Guests
              </Text>
            </Flex>

            <HStack
              spacing={0}
              border="1px solid"
              borderColor={theme.inputBorder}
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              bg={theme.inputBg}
              w="100%"
              h="42px"
            >
              <Button
                h="40px"
                borderRadius="0"
                variant="ghost"
                minW="42px"
                onClick={() => {
                  setGuests((prev) => Math.max(1, prev - 1));
                  resetAvailability();
                }}
                color={theme.text}
                _hover={{ bg: theme.hover }}
              >
                -
              </Button>

              <Box
                flex="1"
                textAlign="center"
                color={theme.text}
                fontWeight="600"
                py={2}
                borderLeft="1px solid"
                borderRight="1px solid"
                borderColor={theme.inputBorder}
                whiteSpace="nowrap"
              >
                {guests}
              </Box>

              <Button
                h="40px"
                borderRadius="0"
                variant="ghost"
                minW="42px"
                onClick={() => {
                  setGuests((prev) => prev + 1);
                  resetAvailability();
                }}
                color={theme.text}
                _hover={{ bg: theme.hover }}
              >
                +
              </Button>
            </HStack>
          </Box>

          <Flex
            gridColumn={{ base: "1 / -1", xl: "auto" }}
            gap={3}
            align="flex-end"
            alignSelf="end"
            justify={{ base: "flex-start", xl: "flex-end" }}
            flexWrap={{ base: "wrap", xl: "nowrap" }}
            whiteSpace="nowrap"
            minW={{ xl: "max-content" }}
          >
            {!!currentUserUid && (
              <Button
                size="sm"
                color="white"
                bgGradient="linear(to-r, gray.800, gray.900)"
                border="1px solid"
                borderColor="whiteAlpha.200"
                borderRadius={cardsBorderRadii}
                fontWeight="700"
                px={5}
                h="42px"
                leftIcon={
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="transform 0.25s ease"
                    className="add-accommodation-icon"
                  >
                    <Plus size={15} />
                  </Box>
                }
                _hover={{
                  bgGradient: "linear(to-r, gray.700, black)",
                  borderColor: "whiteAlpha.300",
                  transform: "translateY(-1px)",
                  boxShadow: theme.shadowMd || theme.shadowSm,
                  ".add-accommodation-icon": {
                    transform: "rotate(90deg)",
                  },
                }}
                _active={{
                  transform: "translateY(0)",
                  boxShadow: theme.shadowSm,
                }}
                onClick={() => {
                  setSelected(null);
                  formModal.onOpen();
                }}
              >
                Add Accommodation
              </Button>
            )}

            {availabilityMode && (
              <Button
                h="42px"
                size="sm"
                variant="outline"
                onClick={clearAvailabilityFilter}
                borderColor={theme.borderStrong}
                borderRadius={cardsBorderRadii}
                color={theme.text}
                _hover={{ bg: theme.hover }}
              >
                Clear filter
              </Button>
            )}
          </Flex>
        </Box>

        {availabilityMode && (
          <Text mt={2} fontSize="sm" color={theme.textSubtle}>
            Showing availability from <b>{checkIn ? toISO(checkIn) : "-"}</b> to{" "}
            <b>{checkOut ? toISO(checkOut) : "-"}</b>.
          </Text>
        )}
      </Box>

      <Divider mt={4} borderColor={theme.divider} />

      <Box p={4} position="relative" zIndex={1}>
        {loadingAccommodations ? (
          <Flex
            minH="calc(100vh - 260px)"
            w="100%"
            align="center"
            justify="center"
          >
            <GlobalSpinner />
          </Flex>
        ) : (
          <>
            <SimpleGrid
              columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
              spacing={4}
              alignItems="stretch"
            >
              {filtered.map((acc) => {
                const hero = acc.images?.[0] || acc.imageUrl || "";
                const { available, statusLabel } =
                  getAccommodationAvailabilityState(acc);

                const canManage = isOwner(acc);
                const pricePerNight = Number(
                  acc.pricePerNight ?? acc.price ?? 0,
                );

                return (
                  <Box key={acc._id} h="100%">
                    <BusinessListingShowcaseCard
                      title={acc.name || "Accommodation"}
                      subtitle={`${acc.location || "-"} • ${
                        acc.type || "Accommodation"
                      }`}
                      description={
                        acc.description ||
                        "Comfortable stay available for booking."
                      }
                      image={hero}
                      price={
                        pricePerNight > 0
                          ? `MWK ${pricePerNight.toLocaleString()} / night`
                          : "Price on request"
                      }
                      location={acc.location || ""}
                      category={acc.type || "Accommodation"}
                      badgeLabel={statusLabel}
                      statusTone={available ? "success" : "warning"}
                      actionLabel={available ? "Book" : "View"}
                      onCardClick={() => openDetails(acc)}
                      onActionClick={() =>
                        handleAccommodationAction(acc, available)
                      }
                      footerActions={
                        canManage ? (
                          <>
                            <IconButton
                              aria-label="Edit accommodation"
                              icon={<EditIcon />}
                              size="sm"
                              variant="outline"
                              borderRadius={cardsBorderRadii}
                              borderColor={theme.borderStrong}
                              color={theme.text}
                              bg={theme.surfaceAlt}
                              _hover={{ bg: theme.hover }}
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelected(
                                  attachOwnerToAccommodation(
                                    acc,
                                    currentUserUid,
                                  ),
                                );
                                formModal.onOpen();
                              }}
                            />

                            <IconButton
                              aria-label="Delete accommodation"
                              icon={<DeleteIcon />}
                              size="sm"
                              borderRadius={cardsBorderRadii}
                              colorScheme="red"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(acc._id);
                              }}
                            />
                          </>
                        ) : undefined
                      }
                    />
                  </Box>
                );
              })}
            </SimpleGrid>

            {filtered.length === 0 && (
              <Box mt={10} textAlign="center" color={theme.textSubtle}>
                No accommodations found.
              </Box>
            )}
          </>
        )}

        {searching && (
          <HStack mt={4} color={theme.textSubtle}>
            <Spinner size="sm" />
            <Text>Checking availability…</Text>
          </HStack>
        )}
      </Box>

      <Modal isOpen={formModal.isOpen} onClose={formModal.onClose}>
        <ModalOverlay bg={theme.overlay} />
        <ModalContent
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
        >
          <ModalHeader color={theme.text}>
            {selected ? "Edit" : "Add"} Accommodation
          </ModalHeader>
          <ModalCloseButton color={theme.text} />
          <ModalBody>
            <AccommodationForm
              initialData={selected}
              onSubmit={selected ? handleUpdate : handleCreate}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <AccommodationDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={detailsModal.onClose}
        accommodation={selected}
        initialCheckIn={checkIn}
        initialCheckOut={checkOut}
        initialGuests={guests}
        onBook={goToCheckout}
      />

      <Modal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedbackModal}
        isCentered
        size={{ base: "sm", md: "md" }}
      >
        <ModalOverlay bg={theme.overlay} />
        <ModalContent
          mx={{ base: 4, md: 0 }}
          bg={theme.surface}
          border="1px solid"
          borderColor="orange.300"
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowMd}
        >
          <ModalHeader color={theme.text}>
            <HStack spacing={3}>
              <Flex
                w="38px"
                h="38px"
                align="center"
                justify="center"
                borderRadius={cardsBorderRadii}
                bg="orange.100"
                color="orange.600"
                flexShrink={0}
              >
                <AlertTriangle size={19} />
              </Flex>
              <Text fontSize="lg" fontWeight="800">
                {feedbackModal.title}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={theme.text} />
          <ModalBody pb={2}>
            <Text color={theme.textMuted} lineHeight="1.7">
              {feedbackModal.description}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={closeFeedbackModal}
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{ bgImage: theme.gradients.primaryButtonHover }}
              borderRadius={cardsBorderRadii}
            >
              Okay
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Accommodations;
